import { Link, useFetcher, useLoaderData } from '@remix-run/react'
import {
	ChevronDown,
	Loader2,
	MessageCircle,
	Minus,
	Plus,
	Send,
	ThumbsDown,
	ThumbsUp,
	User2,
	Zap,
	Trash2,
	X,
	PlusCircle,
	Search,
	Star,
	Gem,
} from 'lucide-react'
import { Img } from 'openimg/react'
import * as React from 'react'
import { toast } from 'sonner'
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from '#app/components/ui/avatar.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card } from '#app/components/ui/card.tsx'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogDescription,
	DialogFooter,
} from '#app/components/ui/dialog.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { UserProfileDialog } from '#app/components/user/user-profile-dialog.tsx'
import { type loader as stationLoader } from '#app/routes/estacoes.$stationId.tsx'
import { cn, getCdnUserImgSrc } from '#app/utils/misc.tsx'
import { ThreadMessageForm } from '../threads/thread-message-form.tsx'
import { ThreadMessages } from '../threads/thread-messages.tsx'
import { ThreadReplyCount } from '../threads/thread-reply-count.tsx'

// Define and Export the Review type (consider moving this to a shared types file)
export type Review = {
	id: number
	comment: string | null
	rating: number | null
	createdAt: string | Date
	kilowatts: number | null
	vehicleName: string | null
	vehicleMake: string | null
	threadMessages?: Array<{
		id: string
		message: string
		createdAt: string
		reviewId: number
		user: {
			username: string | null
			image: {
				id: string
				objectKey: string | null
			} | null
		} | null
	}>
	threadMessageCount?: number
	user: {
		id: string
		username: string | null
		score: number | null
		image: {
			id: string
			objectKey: string | null
		} | null
	} | null
}

// Define the props for ReviewsSection
interface ReviewsSectionProps {
	stationId: number
	stationUrl: string
	initialReviews: Review[]
	totalReviews: number
	user: any // Replace with a specific user type
	reviewFetcher: any // Type this appropriately based on useFetcher
	formRef: React.RefObject<HTMLFormElement>
}

// Define the LoaderData type for reviewsFetcher
type ReviewsLoaderData = {
	station: {
		reviews: Review[]
	} | null
	// Add other properties from the loader if needed
}

const INITIAL_REVIEWS_DISPLAY_COUNT = 3

// Helper Component for rendering a single Review Item
function ReviewItem({
	review,
	user,
	onDeleteClick,
}: {
	review: Review
	user: any // Replace with specific user type
	onDeleteClick: (reviewId: number) => void
}) {
	const formattedDate =
		typeof review.createdAt === 'string'
			? review.createdAt
			: new Date(review.createdAt).toLocaleDateString('pt-BR', {
					hour: '2-digit',
					minute: '2-digit',
				})

	return (
		<div className="rounded-lg border border-border/40 bg-muted/20 p-4 shadow-md">
			{/* User Info, Rating and Date in a single row */}
			<div className="flex items-start justify-between">
				{/* User Info */}
				<div className="group flex items-center gap-3">
					{review.user ? (
						<UserProfileDialog
							userId={review.user.id}
							initialUsername={review.user.username}
							initialImageId={review.user.image?.id}
							trigger={
								<div className="flex cursor-pointer items-center gap-3">
									<Avatar className="h-12 w-12 flex-shrink-0 rounded-md bg-muted transition-opacity group-hover:opacity-80">
										{review.user.image?.id ? (
											<AvatarImage
												src={getCdnUserImgSrc(
													review.user.image.objectKey || '',
												)}
												alt={review.user.username ?? 'Avatar do usuário'}
												className="object-cover"
												width="48"
												height="48"
											/>
										) : (
											<AvatarFallback className="rounded-md bg-muted">
												<User2 className="h-6 w-6 text-muted-foreground" />
											</AvatarFallback>
										)}
									</Avatar>
									<div className="flex flex-col items-start">
										<div className="flex items-center gap-1.5">
											<div className="text-sm font-medium text-foreground group-hover:underline">
												{review.user.username || 'Usuário'}
											</div>
											{user && user.id === review.user?.id && (
												<Button
													variant="ghost"
													size="icon"
													className="h-5 w-5 rounded-md text-destructive/60 transition-opacity hover:bg-destructive/10 hover:text-destructive"
													onClick={(e) => {
														e.stopPropagation() // Prevent dialog trigger
														onDeleteClick(review.id)
													}}
													aria-label="Excluir avaliação"
												>
													<Trash2 className="h-3.5 w-3.5" />
												</Button>
											)}
										</div>
										{review.user.score != null && review.user.score > 0 && (
											<div className="flex w-fit items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs leading-none text-blue-600 transition-colors dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/50">
												<Zap
													fill="currentColor"
													className="mr-0.5 h-2.5 w-2.5 text-blue-500 dark:text-blue-400"
												/>
												{review.user.score}
											</div>
										)}
									</div>
								</div>
							}
						/>
					) : (
						// Fallback for anonymous user - ensure top alignment here too
						<div className="flex items-start gap-3">
							<Avatar className="h-12 w-12 flex-shrink-0 rounded-md">
								<AvatarFallback className="rounded-md bg-muted">
									<User2 className="h-6 w-6 text-muted-foreground" />
								</AvatarFallback>
							</Avatar>
							<div className="pt-1 text-sm font-medium text-muted-foreground">
								Usuário
							</div>
						</div>
					)}
				</div>

				{/* Rating Badge & Date */}
				<div className="flex flex-shrink-0 flex-col items-end gap-1">
					{review.rating === 1 ? (
						<Badge
							variant="outline"
							className="border-green-600 bg-green-100 px-1.5 py-0.5 text-xs text-green-800 dark:border-green-500 dark:bg-green-900/40 dark:text-green-200"
						>
							<ThumbsUp className="mr-1 h-3 w-3 flex-shrink-0" /> OK
						</Badge>
					) : review.rating === 0 ? (
						<Badge
							variant="outline"
							className="border-red-600 bg-red-100 px-1.5 py-0.5 text-xs text-red-800 dark:border-red-500 dark:bg-red-900/40 dark:text-red-200"
						>
							<ThumbsDown className="mr-1 h-3 w-3 flex-shrink-0" /> Falhou
						</Badge>
					) : (
						<Badge
							variant="outline"
							className="border-yellow-600 bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-800 dark:border-yellow-500 dark:bg-yellow-900/40 dark:text-yellow-200"
						>
							<Minus className="mr-1 h-3 w-3 flex-shrink-0" /> Neutro
						</Badge>
					)}
					<p className="text-xs text-muted-foreground">{formattedDate}</p>
				</div>
			</div>

			{/* Comment - Always render this block */}
			<div className="mt-3">
				{review.comment ? (
					<p className="break-words text-sm leading-relaxed text-foreground">
						{review.comment}
					</p>
				) : (
					// Discrete placeholder when comment is missing
					<p className="text-sm italic text-muted-foreground/85">
						(Usuário não deixou um comentário)
					</p>
				)}
			</div>

			{/* Details & Thread Interaction */}
			<div className="mt-3 flex items-center justify-between">
				{/* kW, Vehicle, Add Vehicle Link */}
				<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
					{review.kilowatts != null && (
						<Badge
							variant="secondary"
							className="border border-yellow-200 bg-yellow-50 text-xs font-medium text-yellow-800 transition-colors hover:bg-yellow-50"
						>
							<Zap className="mr-1 h-3 w-3 flex-shrink-0" /> {review.kilowatts}{' '}
							kW
						</Badge>
					)}
					{review.vehicleName ? (
						review.vehicleMake ? (
							<Link
								to={`/carros?marca=${encodeURIComponent(review.vehicleMake.toLowerCase())}`}
								prefetch="intent"
								className="group flex max-w-full items-center justify-center"
							>
								<Badge
									variant="secondary"
									className="truncate bg-muted text-center text-xs font-medium text-accent-foreground group-hover:bg-accent group-hover:underline"
									title={review.vehicleName}
								>
									{review.vehicleName}
								</Badge>
							</Link>
						) : (
							<Badge
								variant="secondary"
								className="max-w-full truncate bg-muted text-center text-xs font-medium text-accent-foreground"
								title={review.vehicleName}
							>
								{review.vehicleName}
							</Badge>
						)
					) : user && user.id === review.user?.id && !user.userCar ? (
						<Link
							to="/garagem"
							className="flex items-center gap-1 text-xs text-primary hover:underline"
						>
							<Plus className="h-3 w-3" />
							Adicionar veículo
						</Link>
					) : null}
				</div>

				{/* Thread Reply Count/Trigger */}
				<Dialog>
					<DialogTrigger asChild>
						<button className="group/reply focus:outline-none">
							<ThreadReplyCount reviewId={review.id} review={review} />
						</button>
					</DialogTrigger>
					<DialogContent className="flex max-h-[80vh] max-w-2xl flex-col">
						<DialogHeader className="border-b pb-3">
							<DialogTitle className="flex items-center gap-2 text-lg font-medium">
								<MessageCircle className="h-5 w-5 text-blue-500" />
								<span>Conversa</span>
								<Badge variant="outline" className="ml-1 font-normal">
									{review.user?.username || 'Usuário'}
								</Badge>
							</DialogTitle>
							<DialogDescription>
								Conversa iniciada por {review.user?.username || 'Usuário'} sobre
								o check-in.
							</DialogDescription>
						</DialogHeader>
						<div className="-mx-1 flex-1 overflow-y-auto px-1 py-4">
							<div className="mb-6 rounded-lg bg-blue-50/50 p-4 shadow-sm dark:bg-blue-900/10">
								<div className="flex gap-3">
									<div className="flex-shrink-0">
										{review.user?.image?.id ? (
											<Img
												src={getCdnUserImgSrc(
													review.user.image.objectKey || '',
												)}
												alt="user avatar"
												className="h-10 w-10 rounded-full object-cover shadow-sm"
												loading="lazy"
												width="40"
												height="40"
											/>
										) : (
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground shadow-sm">
												<User2 className="h-5 w-5" />
											</div>
										)}
									</div>
									<div className="flex-1">
										<div className="mb-1 flex items-center gap-2">
											<p className="text-sm font-semibold">
												{review.user?.username || 'Usuário'}
											</p>
											<div className="flex items-center">
												{review.rating === 1 ? (
													<ThumbsUp className="mr-1 h-3 w-3 text-green-500" />
												) : review.rating === 0 ? (
													<ThumbsDown className="mr-1 h-3 w-3 text-red-500" />
												) : (
													<Minus className="mr-1 h-3 w-3 text-yellow-500" />
												)}
												<p className="text-xs text-muted-foreground">
													{formattedDate}
												</p>
											</div>
										</div>
										<p className="break-words text-sm leading-relaxed">
											{review.comment}
										</p>
										{(review.kilowatts != null || review.vehicleName) && (
											<p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
												{review.kilowatts != null && (
													<Badge variant="secondary" className="text-xs">
														⚡ {review.kilowatts} kW
													</Badge>
												)}
												{review.vehicleName &&
													(review.vehicleMake ? (
														<Link
															to={`/carros?marca=${encodeURIComponent(review.vehicleMake.toLowerCase())}`}
															prefetch="intent"
															className="group"
														>
															<Badge
																variant="secondary"
																className="text-center text-xs group-hover:bg-accent group-hover:underline"
															>
																{review.vehicleName}
															</Badge>
														</Link>
													) : (
														<Badge
															variant="secondary"
															className="text-center text-xs"
														>
															{review.vehicleName}
														</Badge>
													))}
											</p>
										)}
									</div>
								</div>
							</div>
							<div className="pl-2">
								<div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-2 dark:border-gray-800">
									<MessageCircle className="h-4 w-4 text-blue-500" />
									<h4 className="text-sm font-medium">Respostas</h4>
								</div>
								<ThreadMessages reviewId={review.id} review={review} />
							</div>
						</div>
						<div className="border-t pt-4">
							<ThreadMessageForm reviewId={review.id} user={user} />
						</div>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	)
}

export function ReviewsSection({
	stationId,
	stationUrl,
	initialReviews,
	totalReviews,
	user,
	reviewFetcher,
	formRef,
}: ReviewsSectionProps) {
	const [rating, setRating] = React.useState<number | undefined>(undefined)
	const reviewsFetcher = useFetcher<ReviewsLoaderData>()
	const [currentPage, setCurrentPage] = React.useState(1)
	const [reviews, setReviews] = React.useState<Review[]>(initialReviews)
	const [isLoading, setIsLoading] = React.useState(false)
	const deleteReviewFetcher = useFetcher()
	const [reviewToDelete, setReviewToDelete] = React.useState<number | null>(
		null,
	)
	const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
	const processedDeleteIdRef = React.useRef<number | null>(null)
	const [allReviewsDialogOpen, setAllReviewsDialogOpen] = React.useState(false) // State for the main reviews dialog

	// Function to handle loading more reviews manually
	const handleLoadMore = React.useCallback(() => {
		if (
			!isLoading &&
			reviews.length < totalReviews &&
			stationId &&
			stationUrl
		) {
			setIsLoading(true)
			const nextPage = currentPage + 1
			// Use stationUrl directly if it includes the base path, otherwise construct it
			const loadUrl = stationUrl.includes('/estacoes/')
				? `${stationUrl}?page=${nextPage}`
				: `/estacoes/${stationUrl}${stationId}?page=${nextPage}`
			reviewsFetcher.load(loadUrl)
			setCurrentPage(nextPage)
		}
	}, [
		isLoading,
		reviews.length,
		totalReviews,
		stationId,
		stationUrl,
		currentPage,
		reviewsFetcher,
	])

	// Append new reviews loaded by the fetcher
	React.useEffect(() => {
		if (reviewsFetcher.data && reviewsFetcher.state === 'idle') {
			const newReviewsData =
				(reviewsFetcher.data as ReviewsLoaderData).station?.reviews || []

			setReviews((prevReviews) => {
				const uniqueNewReviews = newReviewsData.filter(
					(nr) => !prevReviews.some((r) => r.id === nr.id),
				)
				if (uniqueNewReviews.length > 0) {
					return [...prevReviews, ...uniqueNewReviews]
				}
				return prevReviews
			})

			setIsLoading(false)
		} else if (reviewsFetcher.state === 'idle') {
			setIsLoading(false)
		}
	}, [reviewsFetcher.data, reviewsFetcher.state])

	// Prepend newly submitted review
	React.useEffect(() => {
		const submittedData = reviewFetcher.data as any // Type this correctly
		if (submittedData?.status === 200 && submittedData.newReview && user) {
			const rawReview = submittedData.newReview.reviews[0]
			const formattedCreatedAt = new Date(
				rawReview.createdAt,
			).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' })

			const newReviewToAdd = {
				...rawReview,
				createdAt: formattedCreatedAt,
				threadMessages: [],
				threadMessageCount: 0,
			}

			setReviews((prevReviews) => {
				const reviewExists = prevReviews.some((r) => r.id === newReviewToAdd.id)
				if (!reviewExists) {
					const updatedReviews = [newReviewToAdd, ...prevReviews]
					return updatedReviews
				}
				return prevReviews
			})

			setRating(undefined)

			// Scroll the page to the reviews heading *after* submission
			const reviewsHeading = document.getElementById('reviews-heading')
			if (reviewsHeading) {
				reviewsHeading.scrollIntoView({ behavior: 'smooth', block: 'start' })
			}
		}
	}, [reviewFetcher.data, user]) // Removed dependency on showAllReviews

	// Handle review deletion result
	React.useEffect(() => {
		if (deleteReviewFetcher.data && deleteReviewFetcher.state === 'idle') {
			const response = deleteReviewFetcher.data as any
			if (
				response?.status === 200 &&
				reviewToDelete &&
				reviewToDelete !== processedDeleteIdRef.current
			) {
				setReviews((prev) =>
					prev.filter((review) => review.id !== reviewToDelete),
				)

				toast.success('Avaliação excluída com sucesso', {
					description: '⚠️ Você perdeu 2 pontos.',
				})

				processedDeleteIdRef.current = reviewToDelete
				setReviewToDelete(null)
				// Close the delete confirmation dialog if it's still somehow open
				setDeleteDialogOpen(false)
			} else if (response?.message && response?.status !== 200) {
				toast.error('Erro ao excluir avaliação', {
					description: response.message,
				})
				setReviewToDelete(null)
			}
		}
	}, [deleteReviewFetcher.data, deleteReviewFetcher.state, reviewToDelete])

	const handleDeleteClick = (reviewId: number) => {
		setReviewToDelete(reviewId)
		setDeleteDialogOpen(true)
	}

	return (
		<Card className="overflow-hidden bg-white shadow-sm backdrop-blur-sm">
			<div className="p-5">
				<div className="mb-4 flex items-center gap-2">
					<MessageCircle className="h-5 w-5 text-primary" />
					<h2 id="reviews-heading" className="text-lg font-semibold">
						Últimos Check-ins
					</h2>
				</div>
				<div className="relative">
					{/* Initial Reviews Display (for SEO and immediate view) */}
					<div className="mb-4 w-full pb-2">
						{reviews && reviews.length > 0 ? (
							<div className="space-y-4">
								{reviews
									.slice(0, INITIAL_REVIEWS_DISPLAY_COUNT)
									.map((review, index) => (
										<ReviewItem
											key={review.id}
											review={review}
											user={user}
											onDeleteClick={handleDeleteClick}
										/>
									))}
							</div>
						) : (
							<div className="flex h-[200px] flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-muted/20 p-4 text-center">
								<MessageCircle
									className="h-12 w-12 text-muted-foreground/50"
									strokeWidth={1.5}
								/>
								<p className="mt-4 text-base font-medium text-foreground/80">
									Nenhum check-in ainda
								</p>
								<p className="mt-1 flex items-center justify-center gap-1 text-sm text-muted-foreground">
									Seja o primeiro e ganhe +2 pontos!{' '}
									<Zap
										fill="currentColor"
										className="h-4 w-4 flex-shrink-0 text-blue-500"
									/>
								</p>
							</div>
						)}
					</div>

					{/* Button to open Dialog for All Reviews */}
					{totalReviews > INITIAL_REVIEWS_DISPLAY_COUNT && (
						<div className="flex justify-center">
							<Dialog
								open={allReviewsDialogOpen}
								onOpenChange={(open) => {
									setAllReviewsDialogOpen(open)
									if (!open) {
										// Reset page number when dialog closes
										setCurrentPage(1)
										// Optional: Reset reviews back to initialReviews if you want a fresh load every time.
										// setReviews(initialReviews);
									}
								}}
							>
								<DialogTrigger asChild>
									<Button className="flex w-full items-center justify-center gap-2">
										<Search className="h-5 w-5" />
										<span>Ver todos os {totalReviews} check-ins</span>
									</Button>
								</DialogTrigger>
								<DialogContent className="flex h-[90vh] max-w-2xl flex-col sm:max-w-3xl">
									<DialogHeader className="border-b pb-4">
										<DialogTitle className="text-xl">
											Todos os Check-ins ({totalReviews})
										</DialogTitle>
									</DialogHeader>
									<div className="-mr-3 flex-1 space-y-4 overflow-y-auto py-4 pr-3">
										{reviews.map((review) => (
											<ReviewItem
												key={review.id}
												review={review}
												user={user}
												onDeleteClick={handleDeleteClick}
											/>
										))}

										{/* Load More Button inside Dialog */}
										{reviews.length < totalReviews && (
											<div className="flex justify-center pt-4">
												{isLoading ? (
													<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
												) : (
													<Button
														variant="outline"
														onClick={handleLoadMore}
														disabled={
															isLoading || reviewsFetcher.state !== 'idle'
														}
													>
														Carregar mais check-ins
													</Button>
												)}
											</div>
										)}
										{reviews.length === totalReviews && totalReviews > 0 && (
											<p className="pt-4 text-center text-sm text-muted-foreground">
												Fim dos check-ins.
											</p>
										)}
									</div>
									{/* Optional Footer */}
									{/* <DialogFooter> <Button onClick={() => setAllReviewsDialogOpen(false)}>Fechar</Button> </DialogFooter> */}
								</DialogContent>
							</Dialog>
						</div>
					)}
				</div>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={deleteDialogOpen}
					onOpenChange={(open) => {
						setDeleteDialogOpen(open)
						if (!open) {
							setReviewToDelete(null)
							processedDeleteIdRef.current = null
						}
					}}
				>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>Excluir check-in</DialogTitle>
							<DialogDescription>
								Tem certeza que deseja excluir este check-in? Você perderá 2
								pontos e esta ação não pode ser desfeita.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter className="flex flex-row justify-end gap-2 sm:justify-end">
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setDeleteDialogOpen(false)
									setReviewToDelete(null)
									processedDeleteIdRef.current = null
								}}
							>
								Cancelar
							</Button>
							<Button
								type="button"
								variant="destructive"
								onClick={() => {
									if (reviewToDelete) {
										deleteReviewFetcher.submit(
											{
												intent: 'delete-review',
												reviewId: reviewToDelete.toString(),
												userId: user?.id || '',
												locationId: stationId.toString(),
											},
											{ method: 'post' },
										)
										// Keep delete dialog open until fetcher confirms, handle close in useEffect
									}
								}}
								disabled={deleteReviewFetcher.state === 'submitting'}
							>
								{deleteReviewFetcher.state === 'submitting' ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : null}
								Excluir
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Review Submission Form */}
				<div className="mt-6 border-t border-border/40 pt-5">
					{user ? (
						<reviewFetcher.Form
							ref={formRef}
							encType="multipart/form-data"
							method="post"
							className="space-y-5"
						>
							<h3 className="text-lg font-semibold text-foreground">
								Deixe seu check-in
							</h3>
							<Input type="hidden" name="locationId" value={stationId} />
							<Input type="hidden" name="userId" value={user.id} />
							<Input type="hidden" name="intent" value="review" />
							<Input
								type="hidden"
								name="rating"
								value={rating?.toString() ?? ''}
							/>

							<div>
								<label className="mb-2 block text-sm font-medium text-foreground">
									Você conseguiu carregar?
								</label>
								<div className="flex space-x-3">
									{[
										{
											value: 1,
											label: 'Sim, carreguei!',
											icon: ThumbsUp,
											color: 'green',
										},
										{
											value: 2,
											label: 'Neutro / Outro',
											icon: Minus,
											color: 'yellow',
										},
										{
											value: 0,
											label: 'Não, falhou!',
											icon: ThumbsDown,
											color: 'red',
										},
									].map(({ value, label, icon: Icon, color }) => (
										<button
											key={value}
											type="button"
											onClick={() => setRating(value)}
											className={cn(
												'flex flex-col items-center justify-center rounded-lg border p-3 text-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
												'group/rating relative h-20 w-1/3',
												{
													'border-green-500 bg-green-50 ring-green-500 dark:border-green-600 dark:bg-green-900/30':
														rating === value && color === 'green',
													'border-border hover:border-green-300 hover:bg-green-50/50 hover:shadow-sm dark:hover:border-green-800 dark:hover:bg-green-900/30':
														color === 'green' && rating !== value,

													'border-yellow-500 bg-yellow-50 ring-yellow-500 dark:border-yellow-600 dark:bg-yellow-900/30':
														rating === value && color === 'yellow',
													'border-border hover:border-yellow-300 hover:bg-yellow-50/50 hover:shadow-sm dark:hover:border-yellow-800 dark:hover:bg-yellow-900/30':
														color === 'yellow' && rating !== value,

													'border-red-500 bg-red-50 ring-red-500 dark:border-red-600 dark:bg-red-900/30':
														rating === value && color === 'red',
													'border-border hover:border-red-300 hover:bg-red-50/50 hover:shadow-sm dark:hover:border-red-800 dark:hover:bg-red-900/30':
														color === 'red' && rating !== value,

													'bg-muted/20 dark:bg-muted/10':
														rating === undefined || rating === null,
													'bg-background dark:bg-background':
														rating !== undefined &&
														rating !== null &&
														rating !== value,
												},
											)}
											aria-pressed={rating === value}
											aria-label={label}
										>
											<Icon
												className={cn(
													'mb-1 h-6 w-6 flex-shrink-0 transition-colors duration-200',
													rating === value
														? `text-${color}-600 dark:text-${color}-400`
														: `text-muted-foreground group-hover/rating:text-${color}-500/70 dark:group-hover/rating:text-${color}-400/70`,
												)}
											/>
											<span
												className={cn(
													'text-xs font-medium transition-colors duration-200',
													rating === value
														? `text-${color}-700 dark:text-${color}-300`
														: `text-foreground/80 group-hover/rating:text-${color}-600/90 dark:group-hover/rating:text-${color}-400/90`,
												)}
											>
												{label}
											</span>
										</button>
									))}
								</div>
							</div>

							<div>
								<label
									htmlFor="feedback"
									className="mb-1 block text-sm font-medium text-foreground"
								>
									Conte sua experiência (opcional)
								</label>
								<Textarea
									id="feedback"
									name="comment"
									rows={3}
									placeholder="Comente sua experiência"
									maxLength={600}
									autoComplete="off"
									className="mt-1 block w-full rounded-md border-border bg-muted/20 p-2 shadow-sm focus:border-primary focus:ring focus:ring-primary/20 dark:bg-input/80"
								/>
							</div>

							<div>
								<label
									htmlFor="kilowatts"
									className="mb-1 block text-sm font-medium text-foreground"
								>
									Potência (kW) (opcional)
								</label>
								<div className="relative mt-1 rounded-md shadow-sm">
									<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
										<Zap className="h-4 w-4 text-muted-foreground" />
									</div>
									<Input
										id="kilowatts"
										name="kilowatts"
										type="number"
										step="0.1"
										min="0"
										className="block w-full rounded-md border-border bg-muted/20 pl-10 pr-12 focus:border-primary focus:ring focus:ring-primary/20 dark:bg-input/80"
										placeholder="Máximo de kWs atingidos"
									/>
									<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
										<span className="text-sm text-muted-foreground">kW</span>
									</div>
								</div>
							</div>

							{reviewFetcher.data && reviewFetcher.data.message && (
								<p className="text-sm text-destructive">
									{reviewFetcher.data.message}
								</p>
							)}

							<Button
								type="submit"
								className="flex w-full items-center justify-center gap-2"
								disabled={
									reviewFetcher.state === 'submitting' || rating === undefined
								}
							>
								{reviewFetcher.state === 'submitting' ? (
									<Loader2 className="h-5 w-5 animate-spin" />
								) : (
									<Send className="h-5 w-5" />
								)}
								<span>
									{reviewFetcher.state !== 'idle'
										? 'Enviando Check-in...'
										: 'Enviar Check-in'}
								</span>
							</Button>
						</reviewFetcher.Form>
					) : (
						<div className="rounded-lg border border-primary/20 bg-primary/5 p-6 text-center dark:border-primary/30 dark:bg-primary/10">
							<Zap className="mx-auto mb-3 h-8 w-8 text-primary" />
							<h3 className="dark:text-primary-300 text-lg font-medium text-primary-foreground">
								Participe da Comunidade!
							</h3>
							<p className="mb-4 mt-1 text-sm text-primary/80 dark:text-primary/70">
								Faça login para deixar seu check-in, ganhar pontos e ajudar
								outros motoristas de elétricos.
							</p>
							<Link
								rel="nofollow"
								to={`/login?redirectTo=/estacoes/${stationUrl}${stationId}`}
							>
								<Button
									className="w-full sm:w-auto"
									variant="default"
									size="lg"
								>
									Entrar ou Criar Conta
								</Button>
							</Link>
						</div>
					)}
				</div>
			</div>
		</Card>
	)
}
